import type { CfoFundingProgress, CfoFundingStage } from "@/api/types";

/** The steps that run locally, in order; provider calls are paced apart. */
const LOCAL: CfoFundingStage["key"][] = ["rounds", "plans", "scoring", "cohort"];

/** Short verbs for the step in progress (dock, dialog header). */
export const RUNNING_LABEL: Record<CfoFundingStage["key"], string> = {
  rounds: "Recording the rounds",
  plans: "Planning the collection",
  calls: "Calling the providers",
  scoring: "Scoring the companies",
  cohort: "Ranking the cohort",
};

function settledOf(stage: CfoFundingStage): number {
  return (stage.done ?? 0) + (stage.failed ?? 0);
}

/** Share of the local work settled, each step weighing the same (0 to 1). */
export function overallOf(progress: CfoFundingProgress): number {
  if (progress.complete) return 1;
  const stages = progress.stages.filter((s) => LOCAL.includes(s.key) && (s.total ?? 0) > 0);
  if (!stages.length) return 0;
  return stages.reduce((sum, s) => sum + settledOf(s) / (s.total ?? 1), 0) / stages.length;
}

/** The step that is running now: the local step with the most work queued (the import's
 *  finalization waits behind the collection plans, the cohort behind the scoring), then
 *  the provider calls; null when nothing runs by itself. */
export function currentOf(progress: CfoFundingProgress): CfoFundingStage | null {
  let current: CfoFundingStage | null = null;
  for (const key of LOCAL) {
    const stage = progress.stages.find((s) => s.key === key);
    if (stage && (stage.queued ?? 0) > (current?.queued ?? 0)) current = stage;
  }
  if (current) return current;
  const calls = progress.stages.find((s) => s.key === "calls");
  return calls && (calls.queued ?? 0) > 0 ? calls : null;
}

/** A step that failed (the cohort ranking, a company's scoring). */
export function failedOf(progress: CfoFundingProgress): CfoFundingStage | null {
  return progress.stages.find((s) => s.state === "failed" || (s.failed ?? 0) > 0) ?? null;
}

export function etaLabel(seconds: number | null | undefined): string | null {
  if (!seconds || seconds <= 0) return null;
  if (seconds < 60) return `about ${Math.max(5, Math.round(seconds / 5) * 5)} s left`;
  return `about ${Math.round(seconds / 60)} min left`;
}

export function countOf(stage: CfoFundingStage): string {
  return `${settledOf(stage)} / ${stage.total ?? 0}`;
}

/** "12 credits", "3 quota units" (accounts bill in apollo_credit, api_credit, quota_unit…). */
export function costLabel(cost: number | null | undefined, unit: string | null | undefined) {
  if (!cost) return "—";
  const value = cost < 10 ? cost.toFixed(2).replace(/\.?0+$/, "") : Math.round(cost).toString();
  const name = (unit ?? "").toLowerCase();
  const noun = name.includes("credit")
    ? "credit"
    : name.includes("quota")
      ? "quota unit"
      : name.replace(/_/g, " ") || "unit";
  return `${value} ${noun}${cost === 1 ? "" : "s"}`;
}
